import { BRAND_PALETTE } from '@worldcoo/shared/brand-palette';
import { createContext, use, useMemo, type ReactNode } from 'react';

/**
 * `ink` strokes the lines and `accent` fills the orange shapes. `paper` fills the shapes that hide the lines
 * behind them (a card, a palm, an envelope): it must match what the drawing sits on.
 */
export type ArtColours = { ink: string; accent: string; paper: string };

/** What an animated drawing takes to be recoloured; each one it leaves out keeps the drawing's own. */
export type ArtColourProps = { ink?: string; accent?: string; paper?: string };

const Colours = createContext<ArtColours>({
	ink: BRAND_PALETTE.webInk,
	accent: BRAND_PALETTE.webOrange,
	paper: 'white',
});

export function ArtColoursProvider(props: { colours: ArtColourProps; paper: string; children: ReactNode }) {
	const ink = props.colours.ink ?? BRAND_PALETTE.webInk;
	const accent = props.colours.accent ?? BRAND_PALETTE.webOrange;
	const paper = props.colours.paper ?? props.paper;
	const colours = useMemo(() => ({ ink, accent, paper }), [ink, accent, paper]);
	return <Colours value={colours}>{props.children}</Colours>;
}

export function useArtColours() {
	return use(Colours);
}

/** The round-capped outline every drawing strokes its lines with. */
export function lineOf(ink: string, strokeWidth: number) {
	return { stroke: ink, strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
}
